import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';
import { IDefaultPopup } from '@r1-frontend/ui-react/components/popups/defaultPopup';
import PopupResult from '@r1-frontend/ui-react/components/popups/popupResult';
import { CreatablePhoneSelect, IOption } from '@r1-frontend/ui-react/components/select/creatableSelect';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { TState } from '~/src/store';

import * as ST from './styled';

interface IProps extends IDefaultPopup {
    providerId: number,
    accessToken: string,
    domain: string,
    setPhone: Dispatch<SetStateAction<string>>,
    setIsIncludesPopupOpen?: Dispatch<SetStateAction<boolean>>,
    setIsPhonePopupOpen: Dispatch<SetStateAction<boolean>>,
    onSendResult?: () => void,
    isLoading: boolean,
    title: string,
    description: string,
}

export const SelectPhonePopup = ({
    setPhone,
    setIsIncludesPopupOpen,
    onClose,
    isOpen,
    isLoading,
    setIsPhonePopupOpen,
    title,
    description,
    onSendResult,
}: IProps) => {
    const { clientContacts } = useSelector((state: TState) => state.auth);

    const phoneOptions = useMemo(() => {
        return (clientContacts?.phone?.filter(({ status }) => status === 'confirmed') || []).map((item) => ({
            label: item.value,
            value: `${item.id}`,
            isContactId: true,
        }));
    }, [clientContacts]);

    const [inputValue, setInputValue] = useState<IOption>();
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const [selectedPhone, setSelectedPhone] = useState<IOption>(
        phoneOptions[0] || {
            value: 0,
            label: '',
        },
    );

    const [isNumberUsed, setIsNumberUsed] = useState(false);

    const onInputChange = useCallback((option: IOption) => {
        setInputValue(option);

        const { value } = option;
        const phoneValue = value || '';

        if (phoneValue && phoneValue.length < 11) {
            return;
        }

        const newOption = {
            value,
            label: value,
        };

        setSelectedPhone(newOption);
    }, [phoneOptions]);

    useEffect(() => {
        setPhone(selectedPhone.value);
    }, [selectedPhone]);

    const onClick = useCallback(() => {
        if (setIsIncludesPopupOpen) {
            setIsIncludesPopupOpen(true);
        } else if (onSendResult) {
            onSendResult();
        }
        setIsPhonePopupOpen(false);
    }, [setIsIncludesPopupOpen, onSendResult]);

    return (
        <PopupResult
            onClose={onClose}
            isOpen={isOpen}
            title={title}
        >
            <ST.InnerText>
                {description}
            </ST.InnerText>
            <ST.WrapperFields
                isSelectOpen={isSelectOpen}
                phonesCount={phoneOptions.length + 2}
            >
                <CreatablePhoneSelect
                    options={phoneOptions}
                    value={selectedPhone}
                    inputValue={inputValue?.value}
                    onChange={(option) => {
                        setSelectedPhone(option);
                        setInputValue(undefined);
                        setIsNumberUsed(false);
                    }}
                    onInputChange={onInputChange}
                    createLabel={'Указать новый номер'}
                    placeholder={'Введите телефон'}
                    inputPlaceHolder={'Номер телефона'}
                    onMenuOpen={() => setIsSelectOpen(true)}
                    onMenuClose={() => setIsSelectOpen(false)}
                />
            </ST.WrapperFields>
            <ST.ButtonWrapper>
            <ButtonGroup>
                <Button
                    onClick={onClick}
                    disabled={selectedPhone.value <= 0 || isNumberUsed}
                    Icon={isLoading && <Throbber color={COLORS.BgMain} />}
                >
                    {!isLoading && 'Продолжить'}
                </Button>
            </ButtonGroup>

            </ST.ButtonWrapper>
        </PopupResult>
    );
};
